import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import PaginatedTable from "../library/Pagination";
import ConfirmModal from "../library/ConfirmModal";
import API_BASE_URL from "../../config/api";

export default function AttachmentUpload({ userId, uploadedBy }) {
  const [attachmentTypes, setAttachmentTypes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef();
  const [notification, setNotification] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const token = sessionStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const id = 5; // integer
        const res = await axios.get(`${API_BASE_URL}/api/Config/GeneralMaster/${id}`);
        setAttachmentTypes(res.data);  // axios puts data in res.data

        if (userId) {
          await fetchAttachments();
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, [userId]);


  const fetchAttachments = async () => {
    await axios
      .get(`${API_BASE_URL}/api/Attachments/${userId}`)
      .then((res) => {

        setAttachments(res.data);
      });

  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleBrowse = () => {
    fileInputRef.current.click();
  };

  const handleUpload = async () => {
    if (!file || !selectedType) {
      setNotification({ type: "error", message: `Please select a file and attachment type.` });
      return;
    } else {
      setNotification(null);
    }

    const alreadyExists = attachments.some(a => a.typeId === Number(selectedType));
    if (alreadyExists) {
      setNotification({ type: "error", message: `A file with this attachment type already exists. Delete it first to upload a new one.` });
      return;
    } else {
      setNotification(null);
    }

    const formData = new FormData();
    formData.append("file", file); // matches IFormFile file
    formData.append("refId", userId); // matches long refId
    formData.append("typeId", Number(selectedType)); // ensure int
    formData.append("remarks", ""); // optional
    formData.append("uploadedBy", uploadedBy); // matches long uploadedBy



    try {
      setUploading(true);
      const res = await axios.post(
        `${API_BASE_URL}/api/Attachments/upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      fetchAttachments();
    } catch (err) {
      setNotification({ type: "error", message: `Upload failed: ${err.response?.data || err.message}` });
    } finally {
      setUploading(false);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };


  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/Attachments/${deleteId}`,
        {
          // headers: { "Content-Type": "multipart/form-data" },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchAttachments();
    } catch (err) {
      setNotification({ type: "error", message: `Delete failed: ${err.response?.data || err.message}` });
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const downloadFile = async (filePath) => {
    const response = await fetch(`${API_BASE_URL}/api/Employee/download/${filePath}`, {
      method: "GET",
      // headers: { Authorization: `Bearer ${token}` } // if needed
    });

    if (!response.ok) throw new Error("File not found or server error");

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // Create a temporary link
    const link = document.createElement("a");
    link.href = url;
    link.download = filePath.split("/").pop(); // Extract filename
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  return (
    <div className="flex flex-1 justify-center pb-5">
      <div className="layout-content-container flex flex-col w-full pb-5 flex-1">
        <div className="flex flex-wrap justify-between gap-3 p-4">
          <h2 className="text-[#111518] text-[22px] font-bold leading-tight tracking-[-0.015em] pb-3">
            Upload Documents
          </h2>
        </div>
        {notification && (
          <div
            className={`mb-4 px-4 py-3 rounded flex justify-between items-start ${notification.type === "error"
              ? "bg-red-100 text-red-700"
              : "bg-green-100 text-green-700"
              }`}
          >
            <span>{notification.message}</span>
            <button
              onClick={handleCloseNotification}
              className="ml-4 font-bold text-lg leading-none"
            >
              ×
            </button>
          </div>
        )}
        <div className="flex max-w-[480px] flex-wrap items-end gap-4 px-4 py-3">
          <label className="flex flex-col min-w-40 flex-1">
            <select
              className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-xl text-[#101518] focus:outline-0 focus:ring-0 border border-[#d4dce2] bg-gray-50 focus:border-[#d4dce2] h-14 placeholder:text-[#5c748a] p-[15px] text-base font-normal leading-normal"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="">Select Attachment Type</option>
              {attachmentTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.title}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex flex-col p-4">
          <div className="flex flex-col p-4">
            <div
              className={`flex flex-col items-center gap-6 rounded-xl border-2 border-dashed px-6 py-14 transition-colors ${isDragging ? "border-blue-500 bg-blue-50" : "border-[#d4dce2]"
                }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <div className="flex max-w-[480px] flex-col items-center gap-2">
                <p className="text-[#101518] text-lg font-bold leading-tight tracking-[-0.015em] text-center">
                  Drag and drop files here
                </p>
                <p className="text-[#101518] text-sm font-normal leading-normal text-center">
                  Or
                </p>
              </div>

              <button
                onClick={handleBrowse}
                className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center rounded-xl h-10 px-4 bg-[#eaedf1] text-[#101518] text-sm font-bold"
              >
                <span className="truncate">Browse Files</span>
              </button>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
              />

              {file && (
                <p className="text-[#5c748a] text-sm mt-2 truncate">{file.name}</p>
              )}

              <button
                disabled={uploading}
                onClick={handleUpload}
                className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center rounded-xl h-10 px-4 bg-[#1d4ed8] text-white text-sm font-bold"
              >
                {uploading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </div>

        </div>


        <div className="px-4 py-3 @container">
          <div className="flex overflow-hidden rounded-xl border border-[#dde1e3] bg-white">
            <PaginatedTable
              columns={[
                "File Type",
                "File Name",
                "Uploaded Date",
                "Actions",
              ]}
              data={attachments}
              pageSize={10}
              showPagination={false}
              renderRow={(a, index) => (
                <tr key={a.id || index} className="border-t border-t-[#dde1e3]">
                  <td className="h-[72px] px-4 py-2 text-sm font-normal leading-normal">
                    {attachmentTypes.find((t) => t.id === a.typeId)?.title || "Unknown Type"}
                  </td>
                  <td className="h-[72px] px-4 py-2 text-[#121516] text-sm font-normal leading-normal">
                    {a.fileName}
                  </td>
                  <td className="h-[72px] px-4 py-2 text-[#6a7881] text-sm font-normal leading-normal">
                    {a.uploadDate?.substring(0, 10)}
                  </td>

                  <td className="h-[72px] px-4 py-2 text-sm font-normal leading-normal">
                    <div className="flex gap-2">
                      <button
                        onClick={() => downloadFile(a.filePath)}
                        className="text-blue-600 hover:underline text-sm font-medium cursor-pointer mr-3"
                      >
                        Download
                      </button>
                      <button
                        onClick={() => handleDeleteClick(a.id)}
                        className="text-red-500"
                      >
                        Delete
                      </button>
                    </div>
                  </td>

                </tr>
              )}
            />

          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmOpen}
        title="Delete Attachment"
        message="Are you sure you want to delete this attachment?"
        confirmText="Delete"
        cancelText="Cancel"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
